import vmClient from 'app/services/vmClient';
import { Entity, EntityStringId, IEntityType, IIdentifiableEntity } from 'app/types/entity';
import { TranslationsForEntity, ALL_TRANSLATIONS } from 'app/constants/schemas/translations';
import { LicensePartner, LicensePartnerMarshaller } from 'app/constants/enums/LicensePartner';
import { IOrganizationSiteFeature, OrganizationSiteFeature } from './SiteFeatures/OrganizationSiteFeature';

export interface IOrganizationAttributes {
  id: EntityStringId;
  /**
   * Collection containing the list of partners this organization has licensed content from.
   */
  licensePartners: LicensePartner[];
  /**
   * The name of the company this organization belongs to.
   * INTERNAL USE PRIMARILY
   */
  companyName: string;
  /**
   * A flag specifying if this Organization is first party.
   * @deprecated -- This flag should generally not be used to configure site behavior.
   * Wherever possible, customizations to the site should flow throuhg the site customizations.
   */
  firstParty: boolean;
}

export interface IOrganizationAssociations {
  /**
   * Collection of site features enabled in the Organization's brands.
   */
  siteFeatures: IOrganizationSiteFeature[];
}
export interface IOrganization extends IIdentifiableEntity {
  attributes: IOrganizationAttributes;
  associations: IOrganizationAssociations;
}

export const OrganizationAttributeDefinitions = {
  firstParty: {
    type: 'boolean',
    notNull: true,
  },
  licensePartners: {
    type: LicensePartner,
    marshaller: LicensePartnerMarshaller,
    collection: true,
    notNull: true,
  },
  companyName: {
    type: 'string',
    notNull: true,
  },
};

export const OrganizationAssociationDefintions = {
  siteFeatures: {
    type: OrganizationSiteFeature,
    notNull: true,
    collection: true,
  },
};

export type Organization = Entity<IOrganization>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Organization: IEntityType<IOrganization> = vmClient.defineClass({
  typeName: 'Organization',
  version: 6,
  persisted: true,
  lockable: true,
  attributes: OrganizationAttributeDefinitions,
  associations: OrganizationAssociationDefintions,
});

export const OrganizationTranslations: Partial<TranslationsForEntity<IOrganization>> = {
  id: {
    getLabel: t => t('entity-fields.Organization.id.label'),
  },
  licensePartners: {
    getLabel: t => t('entity-fields.Organization.licensePartners.label'),
  },
  siteFeatures: {
    getLabel: (t) => t('entity-fields.Organization.siteFeatures.label'),
  },
  companyName: {
    getLabel: (t) => t('entity-fields.Organization.companyName.label'),
  },
  firstParty: {
    getInlineLabel: (t) => t('entity-fields.Organization.firstParty.inline-label'),
    getDescription: (t) => t('entity-fields.Organization.firstParty.description'),
    getLabel: () => '',
  },
};

ALL_TRANSLATIONS.set(Organization, OrganizationTranslations);
